import React, { useState, useEffect, SetStateAction } from "react";
import {
  DndContext,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSquarePlus, faSquareMinus } from "@fortawesome/pro-light-svg-icons";
import {
  faGripDotsVertical,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/pro-solid-svg-icons";
import {
  faMessageCaptions,
  faGear,
  faCheck,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { UnassignedServicesSection } from "./components/UnassignedServicesSection";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import ConfirmationModal from "@components/Modals/ConfirmationModal";
import { TextField } from "@components/FormsV2";
import {
  useCreateProjectServiceMutation,
  useDeleteProjectPhaseMutation,
  useDeleteProjectServiceMutation,
  useDeleteProjectSubserviceMutation,
  useUpdateProjectServiceMutation,
  useCreateProjectSubserviceMutation,
  useUpdateProjectSubserviceMutation,
  useUpdateProjectPhaseMutation,
  useDuplicateProjectServiceByIdMutation,
  useSyncrhonizeStandardProjectServiceByIdMutation,
  useRefreshProjectServiceByIdMutation,
} from "@generated";
import { toCamelCase } from "@utils/helpers";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import {
  ProjectService as Service,
  ProjectSubservice as Subservice,
  ProjectPhase as Phase,
  ColumnDef,
  Project,
} from "../../types";
import ManageNotesSlideout from "../ManageNotesSlideout";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";
import useFlags from "@common/hooks/useFlags";
import { ServicesTable } from "../../../Services/ServicesTable/ServicesTable";

type ExpandedState = {
  phases: Record<string, boolean>;
  services: Record<string, boolean>;
};

interface Props {
  phases: Phase[];
  setPhases: (phases: Phase[]) => void;
  prevPhasesRef: React.MutableRefObject<Phase[]>;
  refetchPhases: () => void;
  services: Service[];
  setServices: (services: Service[]) => void;
  prevServicesRef: React.MutableRefObject<Service[]>;
  refetchServices: () => void;
  columnDefs: ColumnDef<Service>[];
  service_type: "professional_services" | "managed_services";
  draggable?: boolean;
  selectable?: boolean;
  showNotes?: boolean;
  showSettings?: boolean;
  project?: Project;
  expanded: ExpandedState;
  setExpanded: React.Dispatch<SetStateAction<ExpandedState>>;
}

export const ServiceEditorV2: React.FC<Props> = ({
  phases,
  setPhases,
  prevPhasesRef,
  refetchPhases = () => null,
  services,
  setServices,
  prevServicesRef,
  refetchServices = () => null,
  columnDefs,
  draggable = false,
  selectable = false,
  service_type,
  showNotes = false,
  showSettings = false,
  project,
  expanded,
  setExpanded,
}) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const { configHideEmptyPhases } = useFlags();
  const isManagedServices = service_type === "managed_services";
  const visiblePhases =
    configHideEmptyPhases && !isManagedServices
      ? phases.filter((phase) => services.some((s) => s.phase == phase.id))
      : phases;
  //activeId => dragged Item
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draggingPhase, setDraggingPhase] = useState<boolean>(false);
  const [openMenuItem, setOpenMenuItem] = useState<Phase | Service | null>(
    null
  );

  useEffect(() => {
    if (!openMenuItem) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest(".context-menu-container") &&
        !target.closest(".fa-gear")
      ) {
        setOpenMenuItem(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [openMenuItem]);
  const [showRenamePhaseModal, setShowRenamePhaseModal] =
    useState<boolean>(false);
  const [newPhaseName, setNewPhaseName] = useState<string>("");
  const [phaseActionTarget, setPhaseActionTarget] = useState<Phase | null>(
    null
  );
  const [showDeletePhaseModal, setShowDeletePhaseModal] =
    useState<boolean>(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);
  const [showFailAlert, setShowFailAlert] = useState<boolean>(false);
  const [responseMessage, setResponseMessage] = useState<string>("");
  const [updatePhase] = useUpdateProjectPhaseMutation();
  const [deletePhase] = useDeleteProjectPhaseMutation();
  const [updateService] = useUpdateProjectServiceMutation();
  const sensors = useSensors(useSensor(PointerSensor));
  const { hasPrivilege } = usePrivilegeCheck();
  const manageServicesPrivilege = hasPrivilege("projects.tasks", [
    "manage",
    "adjust_standards",
  ]);
  useEffect(() => {
    if (draggingPhase) {
      requestAnimationFrame(() => {
        const collapsed: Record<string, boolean> = {};
        phases.forEach((p) => (collapsed[p.id] = false));
        setExpanded({ ...expanded, phases: collapsed });
      });
    }
  }, [draggingPhase]);

  const handleDragStart = (event: DragStartEvent) => {
    if (!manageServicesPrivilege || project?.status !== "building") return;

    setActiveId(event.active.id.toString());

    const [type] = event.active.id.toString().split(":");
    if (type === "phase") {
      setDraggingPhase(true);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setDraggingPhase(false);
    if (!over) return;

    const [type, id] = active.id.toString().split(":");
    const [overType, overId] = over.id.toString().split(":");

    // === Phase Reorder ===
    if (type === "phase" && overType === "phase") {
      const oldIndex = phases.findIndex((p) => p.id === id);
      const newIndex = phases.findIndex((p) => p.id === overId);
      const reordered = arrayMove(phases, oldIndex, newIndex).map((p, i) => ({
        ...p,
        position: i + 1,
      }));
      setPhases(reordered);
      const prevPhases = prevPhasesRef?.current;
      const changed = reordered.filter(
        (newPhase) =>
          newPhase.position !==
          prevPhases?.find((p) => p.id === newPhase.id)?.position
      );

      if (!changed.length) return;

      for (const phase of changed) {
        const data = {
          id: Number(phase.id),
          type: "project-phases",
          attributes: {
            position: phase.position,
          },
        };
        const response = await updatePhase({
          slug: accountSlug,
          id: Number(phase.id),
          //@ts-ignore
          body: { data },
        });
      }
      // Save the latest order to reference
      prevPhasesRef.current = [...phases];
      setResponseMessage("Phase ordering saved");
      setShowSuccessAlert(true);
      return;
    }

    // === Service Reorder ===
    if (type === "service") {
      const activeService = services.find((s) => s.id === id);
      if (!activeService) return;

      let updated = [...services];
      // Dropped on a phase container
      if (overType === "phase") {
        updated = services.map((s) =>
          s.id === activeService.id
            ? { ...s, phase: overId, position: 9999 }
            : s
        );
      }

      // Dropped on another service
      if (overType === "service") {
        const overService = services.find((s) => s.id === overId);
        if (!overService) return;

        // Don't allow dropping onto or after a pending service
        let effectiveOver = overService;
        if (overService.isPending) {
          const targetPhase = overService.phase;
          const savedInPhase = services.filter(
            (s) => s.phase === targetPhase && !s.isPending
          );
          if (!savedInPhase.length) return;
          effectiveOver = savedInPhase[savedInPhase.length - 1];
        }

        const oldIndex = services.findIndex((s) => s.id === activeService.id);
        const newIndex = services.findIndex((s) => s.id === effectiveOver.id);
        updated = arrayMove(services, oldIndex, newIndex);

        // If dragged across phases, update phase
        if (activeService.phase !== effectiveOver.phase) {
          updated = updated.map((s) =>
            s.id === activeService.id ? { ...s, phase: effectiveOver.phase } : s
          );
        }
      }

      const normalized = normalizeServicePositions(updated);
      const prevServices = prevServicesRef?.current;
      const changed = normalized.filter(
        (service) =>
          service.position !==
          prevServices?.find((p) => p.id === service.id)?.position
      );

      if (!changed.length) return;

      // Save the latest order to reference
      prevServicesRef.current = [...normalized];
      setServices(normalized);

      for (const service of changed) {
        let data: {
          id: number;
          type: string;
          attributes: { position: number };
          relationships?: {
            "project-phase"?: { data: { id: string; type: string } };
          };
        } = {
          id: Number(service.id),
          type: "project-services",
          attributes: {
            position: service.position,
          },
        };
        if (!isManagedServices && service.phase) {
          data.relationships = {
            "project-phase": {
              data: { id: service.phase, type: "project-phases" },
            },
          };
        }
        const response = await updateService({
          slug: accountSlug,
          id: Number(data.id),
          //@ts-ignore
          body: { data },
        });
      }
      setResponseMessage("Service ordering saved");
      setShowSuccessAlert(true);
    }
  };
  // Normalize service positions per phase
  const normalizeServicePositions = (items: Service[]) => {
    const grouped: Record<number, Service[]> = {};
    items.forEach((s) => {
      if (!grouped[s.phase]) grouped[s.phase] = [];
      grouped[s.phase].push(s);
    });
    const flattened: Service[] = [];
    Object.values(grouped).forEach((group) => {
      group.forEach((s, i) => {
        flattened.push({ ...s, position: i + 1 });
      });
    });
    return flattened;
  };

  // === DragOverlay Content ===
  const activeService = activeId?.startsWith("service:")
    ? services.find((s) => `service:${s.id}` === activeId)
    : null;
  const activePhase = activeId?.startsWith("phase:")
    ? phases.find((p) => `phase:${p.id}` === activeId)
    : null;

  const getPhaseMenu = (phase) => {
    return (
      <div className="cogMenu">
        <div
          className="cogMenuItem"
          onClick={() => {
            setPhaseActionTarget(phase);
            setShowRenamePhaseModal(true);
          }}
        >
          Rename
        </div>
        {services?.find((s) => s.phase === phase.id) ? (
          <div className="cogMenuItem disabled">
            Remove all services to delete phase
          </div>
        ) : (
          <div
            className="cogMenuItem"
            onClick={() => {
              setPhaseActionTarget(phase);
              setShowDeletePhaseModal(true);
            }}
          >
            Delete
          </div>
        )}
      </div>
    );
  };
  const closePhaseModal = () => {
    setNewPhaseName("");
    setOpenMenuItem(null);
    setPhaseActionTarget(null);
    setShowRenamePhaseModal(false);
    setShowDeletePhaseModal(false);
  };
  return (
    <>
      {!isManagedServices &&
        (() => {
          const orphanedServices = services.filter(
            (s) => !phases.some((p) => p.id == s.phase)
          );
          return orphanedServices.length > 0 ? (
            <UnassignedServicesSection
              orphanedServices={orphanedServices}
              phases={phases}
              services={services}
              setServices={setServices}
            />
          ) : null;
        })()}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={visiblePhases.map((p) => `phase:${p.id}`)}
          strategy={verticalListSortingStrategy}
        >
          {!isManagedServices ? (
            visiblePhases.map((phase) => (
              <SortablePhase
                key={phase.id}
                phase={phase}
                expanded={expanded}
                setExpanded={setExpanded}
                openMenuItem={openMenuItem}
                setOpenMenuItem={setOpenMenuItem}
                getPhaseMenu={getPhaseMenu}
                services={services}
                setServices={setServices}
                prevServicesRef={prevServicesRef}
                refetchServices={refetchServices}
                columnDefs={columnDefs}
                draggable={draggable}
                selectable={selectable}
                showSettings={showSettings}
                showNotes={showNotes}
                project={project}
              />
            ))
          ) : (
            <PhaseServiceTable
              services={
                services.length
                  ? services
                  : [
                      {
                        id: "-1",
                        name: "No managed services",
                        position: 1,
                        phase: "-1",
                        subservices: [],
                      },
                    ]
              }
              setServices={setServices}
              columnDefs={columnDefs}
              phaseId={"-1"}
              draggable={draggable}
              selectable={selectable}
              prevServicesRef={prevServicesRef}
              showSettings={showSettings}
              showNotes={showNotes}
              openMenuItem={openMenuItem}
              setOpenMenuItem={setOpenMenuItem}
              refetchServices={refetchServices}
              project={project}
              expanded={expanded}
              setExpanded={setExpanded}
            />
          )}
        </SortableContext>

        <DragOverlay adjustScale={false}>
          {activePhase ? (
            <PhaseOverlay phase={activePhase} />
          ) : activeService ? (
            <ServiceOverlay
              service={activeService}
              columnDefs={columnDefs}
              selectable={selectable}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
      {configHideEmptyPhases && !isManagedServices && (
        <h3 className="hideEmptyPhasesNote">
          Note: Only phases with assigned services shown. Adding services
          assigned to phases will cause them to be displayed.
        </h3>
      )}
      {/* Rename Phase Modal */}
      <ScopeStackModal
        modalTitle="Rename Phase"
        modalBody={
          <div>
            <div style={{ marginBottom: "15px" }}>
              Please provide the new phase name
            </div>
            <TextField
              id="phaseName"
              label="Phase Name"
              defaultValue={
                phases?.find((p) => String(p.id) === openMenuItem?.id)?.name
              }
              placeholder="Enter a name for this phase"
              onChange={(e) => setNewPhaseName(e.target.value)}
              required
            />
          </div>
        }
        handleClose={closePhaseModal}
        show={showRenamePhaseModal}
        button1Text="Cancel"
        handleButton1Click={closePhaseModal}
        button2Text="Rename"
        handleButton2Click={async () => {
          if (!phaseActionTarget?.id) return;
          const data = {
            id: Number(phaseActionTarget.id),
            type: "project-phases",
            attributes: { name: newPhaseName },
          };

          const response = await updatePhase({
            slug: accountSlug,
            id: data.id,
            //@ts-ignore
            body: { data },
          });

          //@ts-ignore
          if (response?.data?.data?.id) {
            refetchPhases();
            closePhaseModal();
            setResponseMessage("Phase renamed");
            setShowSuccessAlert(true);
          } else {
            setResponseMessage("Error saving phase name");
            setShowFailAlert(true);
          }
        }}
        button2Disabled={!newPhaseName}
        className="projectPhaseModal"
      />
      {/* Delete Phase Confimation */}
      <ConfirmationModal
        show={showDeletePhaseModal}
        title="Delete Phase"
        message="Are you sure you want to delete this phase?"
        onConfirm={async () => {
          if (!phaseActionTarget?.id) return;
          const response = await deletePhase({
            slug: accountSlug,
            id: Number(phaseActionTarget.id),
          });

          //@ts-ignore
          if (response?.error) {
            setResponseMessage("Error deleting phase");
            setShowFailAlert(true);
          } else {
            refetchPhases();
            closePhaseModal();
            setPhaseActionTarget(null);
            setResponseMessage("Phase deleted");
            setShowSuccessAlert(true);
          }
        }}
        onCancel={() => {
          closePhaseModal();
          setPhaseActionTarget(null);
        }}
      />
      {handleAlert(
        showFailAlert,
        responseMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      {handleAlert(
        showSuccessAlert,
        responseMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
    </>
  );
};

// ===== Overlay Components =====
const PhaseOverlay = ({ phase }: { phase: Phase }) => (
  <div className="projectPhase dragging">
    <div className="phaseHeader">
      <div className="phaseTitle">
        <FontAwesomeIcon className="gripIcon" icon={faGripDotsVertical} />
        <div>{phase.name}</div>
        <FontAwesomeIcon className="gearIcon" icon={faGear} />
      </div>
      <FontAwesomeIcon className="expandIcon" icon={faChevronUp} />
    </div>
  </div>
);

const ServiceOverlay = ({
  service,
  columnDefs,
  selectable,
}: {
  service: Service;
  columnDefs: ColumnDef<Service>[];
  selectable: boolean;
}) => {
  return (
    <table className="serviceTable">
      <tbody>
        <tr className="projectService dragging">
          {columnDefs.map((col, i) => (
            <td
              key={col.id}
              className={`${toCamelCase(col.id)}Col ${col?.className || ""}`}
            >
              <div className="serviceTd">
                {i === 0 && (
                  <span className="dndHandle">
                    <FontAwesomeIcon icon={faGripDotsVertical} />
                  </span>
                )}
                {i === 0 && selectable && (
                  <input type="checkbox" checked={service.checked} readOnly />
                )}
                {i === 0 && service?.type === "project-services" && (
                  <span style={{ cursor: "default" }}>
                    <FontAwesomeIcon icon={faSquarePlus} size="sm" />
                  </span>
                )}
                {col.cell(service)}
              </div>
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
};

// ===== SortablePhase =====
const SortablePhase = ({
  phase,
  services,
  setServices,
  refetchServices,
  expanded,
  setExpanded,
  openMenuItem,
  setOpenMenuItem,
  getPhaseMenu,
  columnDefs,
  draggable,
  selectable,
  prevServicesRef,
  showSettings,
  showNotes,
  project,
}: {
  phase: Phase;
  services: Service[];
  setServices: (services) => void;
  refetchServices: () => void;
  expanded: ExpandedState;
  setExpanded: React.Dispatch<SetStateAction<ExpandedState>>;
  openMenuItem: Service | Phase | null;
  setOpenMenuItem: (item) => void;
  getPhaseMenu: (phase) => void;
  columnDefs: ColumnDef<Service>[];
  draggable: boolean;
  selectable: boolean;
  prevServicesRef: React.MutableRefObject<Service[]>;
  showSettings: boolean;
  showNotes: boolean;
  project?: Project;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: `phase:${phase.id}` });
  const { hasPrivilege } = usePrivilegeCheck();
  const managePhasePrivilege = hasPrivilege("projects.project_phases", [
    "manage",
  ]);
  const isExpanded = expanded.phases[phase.id] ?? true;
  const editEnabled = managePhasePrivilege && project?.status === "building";

  return (
    <div
      key={phase.id}
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className="projectPhase"
    >
      <div className={`phaseHeader ${editEnabled ? "" : "noDnD"}`}>
        <div className="phaseTitle">
          <div className="phaseDragHandle" {...attributes} {...listeners}>
            {editEnabled && (
              <FontAwesomeIcon className="gripIcon" icon={faGripDotsVertical} />
            )}
            <div style={{ color: "#1c2655" }}>{phase.name}</div>
          </div>

          {editEnabled && showSettings && (
            <div style={{ position: "relative", marginLeft: "auto" }}>
              <>
                <FontAwesomeIcon
                  icon={faGear}
                  onClick={(e) => {
                    e.stopPropagation(); // prevent drag event interference
                    setOpenMenuItem((prev) => (prev ? null : phase));
                  }}
                  style={{ cursor: "pointer" }}
                />
                {openMenuItem?.type?.includes("phases") &&
                  openMenuItem?.id == phase.id &&
                  getPhaseMenu(phase)}
              </>
            </div>
          )}
        </div>
        <FontAwesomeIcon
          className="expandIcon"
          icon={isExpanded ? faChevronUp : faChevronDown}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((prev) => ({
              ...prev,
              phases: { ...prev.phases, [phase.id]: !prev.phases[phase.id] },
            }));
          }}
        />
      </div>

      {isExpanded && (
        <div className="phaseBody">
          <PhaseServiceTable
            services={services}
            setServices={setServices}
            columnDefs={columnDefs}
            phaseId={phase.id}
            draggable={draggable}
            selectable={selectable}
            prevServicesRef={prevServicesRef}
            showSettings={showSettings}
            showNotes={showNotes}
            openMenuItem={openMenuItem}
            setOpenMenuItem={setOpenMenuItem}
            refetchServices={refetchServices}
            project={project}
            expanded={expanded}
            setExpanded={setExpanded}
          />
        </div>
      )}
    </div>
  );
};

const PhaseServiceTable = ({
  services,
  setServices,
  refetchServices,
  columnDefs,
  phaseId,
  draggable,
  selectable,
  prevServicesRef,
  showSettings,
  showNotes,
  openMenuItem,
  setOpenMenuItem,
  project,
  expanded,
  setExpanded,
}: {
  services: Service[];
  setServices: (services) => void;
  refetchServices: () => void;
  columnDefs: ColumnDef<Service>[];
  phaseId: string;
  draggable: boolean;
  selectable: boolean;
  prevServicesRef: React.MutableRefObject<Service[]>;
  showSettings: boolean;
  showNotes: boolean;
  openMenuItem: Service | Subservice | null;
  setOpenMenuItem: (item) => void;
  project?: Project;
  expanded: ExpandedState;
  setExpanded: React.Dispatch<SetStateAction<ExpandedState>>;
}) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);

  const [updateSubservice] = useUpdateProjectSubserviceMutation();
  const [createSubservice] = useCreateProjectSubserviceMutation();
  const [createService] = useCreateProjectServiceMutation();
  const [deleteService] = useDeleteProjectServiceMutation();
  const [deleteSubservice] = useDeleteProjectSubserviceMutation();
  const [showDeleteServiceModal, setShowDeleteServiceModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Service | Subservice | null>(
    null
  );
  const [showSaveAllModal, setShowSaveAllModal] = useState(false);
  const [pendingToSave, setPendingToSave] = useState<{
    item: Service | Subservice;
    parentServiceId?: string;
  } | null>(null);
  const [saveModalLoading, setSaveModalLoading] = useState(false);
  const [serviceActionTarget, setServiceActionTarget] =
    useState<Service | null>(null);
  const [showSaveStandardModal, setShowSaveStandardModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState<{
    all_locations: boolean;
  } | null>(null);
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [openNotesItem, setOpenNotesItem] = useState<Service | null>(null);
  const [duplicateService] = useDuplicateProjectServiceByIdMutation();
  const [synchronizeStandard] =
    useSyncrhonizeStandardProjectServiceByIdMutation();
  const [refreshService] = useRefreshProjectServiceByIdMutation();
  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);
  const [showFailAlert, setShowFailAlert] = useState<boolean>(false);
  const [responseMessage, setResponseMessage] = useState<string | string[]>("");
  const [loading, setLoading] = useState<boolean>(false);

  const { hasPrivilege } = usePrivilegeCheck();
  const manageServicePrivilege = hasPrivilege("projects.tasks", [
    "manage",
    "adjust_standards",
  ]);
  const manageCustomServicePrivilege = hasPrivilege("projects.custom_tasks", [
    "manage",
    "adjust_standards",
  ]);
  const manageStandardPrivilege = hasPrivilege("settings.tasks", ["manage"]);

  const multipleProjectLocations = project && project.locations?.length > 1;
  const isManagedServices = phaseId === "-1";

  const sortByPosition = (items: Service[]) =>
    [...items].sort((a, b) => {
      if (a.isPending && !b.isPending) return 1;
      if (!a.isPending && b.isPending) return -1;
      return (a.position || 0) - (b.position || 0);
    });

  const servicesOnPhase = sortByPosition(
    isManagedServices
      ? services
      : services?.filter((s) => s.phase == phaseId) || []
  );

  useEffect(() => {
    const openNotesFor = new URLSearchParams(window.location.search).get(
      "openNotesFor"
    );
    if (!openNotesFor || !servicesOnPhase.length) return;
    const target = servicesOnPhase.find((s) => String(s.id) === openNotesFor);
    if (target) {
      setOpenNotesItem(target);
      const url = new URL(window.location.href);
      url.searchParams.delete("openNotesFor");
      window.history.replaceState({}, "", url.toString());
    }
  }, [servicesOnPhase]);

  const updateSubservicePosition = async (reordered, serviceId) => {
    const previous = prevServicesRef.current?.find(
      (s) => s.id == serviceId
    )?.subservices;
    prevServicesRef.current = prevServicesRef?.current?.map((s) => {
      if (s.id == serviceId) {
        return { ...s, subservices: reordered };
      }
      return s;
    });

    const changed = reordered.filter(
      (sub) => sub.position !== previous?.find((p) => p.id === sub.id)?.position
    );
    if (!changed.length) return;

    setServices(
      services.map((s) => {
        if (s.id == serviceId) {
          return { ...s, subservices: reordered };
        }
        return s;
      })
    );

    for (const sub of changed) {
      const data = {
        id: Number(sub.id),
        type: "project-subservices",
        attributes: {
          name: sub.name,
          position: sub.position,
        },
        relationships: {
          "project-service": {
            data: { id: serviceId, type: "project-services" },
          },
        },
      };

      const res = await updateSubservice({
        slug: accountSlug,
        id: Number(sub.id),
        //@ts-ignore
        body: { data },
      });
    }
    setResponseMessage("Subservice ordering saved");
    setShowSuccessAlert(true);
  };

  const handleAddPending = (serviceId?: string) => {
    const tempId = `pending-${Date.now()}`;
    if (!serviceId) {
      // Add service
      const pendingService: Service = {
        id: tempId,
        type: "project-services",
        name: "",
        position: 9999,
        phase: phaseId,
        subservices: [],
        isPending: true,
        taskSource: "custom",
        location: multipleProjectLocations
          ? project?.locations?.[0]
          : undefined,
        serviceType:
          phaseId === "-1" ? "managed_services" : "professional_services",
      };
      setServices([...services, pendingService]);
    } else {
      const service = services.find((s) => s.id == serviceId);
      // Adding subservice
      const pendingSubservice: Subservice = {
        id: tempId,
        type: "project-subservices",
        name: "",
        position: 9999,
        isPending: true,
        taskSource: "custom",
        resource: service?.resource,
        serviceType:
          phaseId === "-1" ? "managed_services" : "professional_services",
      };
      setServices(
        services.map((s) =>
          s.id == serviceId
            ? {
                ...s,
                subservices: [...(s.subservices || []), pendingSubservice],
              }
            : s
        )
      );
      setExpanded((prev) => ({
        ...prev,
        services: { ...prev.services, [serviceId]: true },
      }));
    }
  };

  const handleSavePending = async (
    pending: Service | Subservice,
    parentServiceId?: string
  ) => {
    const isService = !parentServiceId;

    const missingFields: string[] = [];
    if (!pending.name?.trim()) missingFields.push("Name");
    if (isService && !(pending as Service).category)
      missingFields.push("Category");
    if (isService && multipleProjectLocations && !(pending as Service).location)
      missingFields.push("Location");
    if (!pending.quantity) missingFields.push("Quantity");
    if (!pending.hours) missingFields.push("Hours");
    if (pending.serviceType === "professional_services" && !pending.resource)
      missingFields.push("Resource");

    if (missingFields.length) {
      setResponseMessage(
        `Please fill in required fields: ${missingFields.join(", ")}`
      );
      setShowFailAlert(true);
      return;
    }

    const isMS = isService && window.location.href.includes("managed");
    const parentType = isService ? "project-phase" : "project-service";
    const parentId = isService ? phaseId : parentServiceId;
    const hook = isService ? createService : createSubservice;
    const position = isService
      ? services.filter((s) => s.phase == phaseId && !s.isPending).length + 1
      : (
          services.find((s) => s.id === parentServiceId)?.subservices || []
        ).filter((sub) => !sub.isPending).length + 1;

    let data: any = {
      type: `project-${isService ? "services" : "subservices"}`,
      attributes: {
        name: pending.name || "",
        "service-type": isMS ? "managed_services" : undefined,
        "task-source": "custom",
        quantity: pending.quantity || undefined,
        "override-hours": pending.hours ? Number(pending.hours) : undefined,
        position,
      },
      relationships: {
        [parentType]: !isMS
          ? { data: { id: parentId, type: `${parentType}s` } }
          : undefined,
      },
    };

    if (isService) {
      data.relationships.project = {
        data: { id: Number(project?.id), type: "projects" },
      };
      if ((pending as Service).category) {
        data.relationships["service-category"] = {
          data: {
            id: Number((pending as Service).category!.value),
            type: "service-categories",
          },
        };
      }
      if ((pending as Service).location) {
        data.relationships["project-location"] = {
          data: {
            id: Number((pending as Service).location!.value),
            type: "project-locations",
          },
        };
      }
    }

    if (pending.resource) {
      data.relationships["project-resource"] = {
        data: { id: pending.resource.value, type: "project-resources" },
      };
    }

    //@ts-ignore
    const response = await hook({ slug: accountSlug, body: { data } });
    //@ts-ignore
    const updated = response?.data?.data;
    if (updated) {
      const atts = updated?.attributes;
      const savedItem = {
        ...pending,
        id: updated.id,
        name: atts.name || "",
        position: atts?.position || position,
        taskSource: atts?.["task-source"],
        isPending: false,
      };
      setServices(
        isService
          ? services.map((s) => (s.id === pending.id ? savedItem : s))
          : services.map((s) =>
              s.id == parentServiceId
                ? {
                    ...s,
                    subservices: s.subservices?.map((sub) =>
                      sub.id === pending.id ? savedItem : sub
                    ),
                  }
                : s
            )
      );
      setResponseMessage(`${isService ? "Service" : "Subservice"} added`);
      setShowSuccessAlert(true);
      //@ts-ignore
    } else if (response.error) {
      setResponseMessage(
        `Something went wrong adding a ${isService ? "service" : "subservice"}`
      );
      setShowFailAlert(true);
    }
  };

  const handleCheckAndSave = (
    item: Service | Subservice,
    parentServiceId?: string
  ) => {
    const pendingServiceCount = services.filter((s) => s.isPending).length;
    const pendingSubserviceCount = services.reduce(
      (acc, s) =>
        acc + (s.subservices?.filter((sub) => sub.isPending).length || 0),
      0
    );
    const totalPending = pendingServiceCount + pendingSubserviceCount;
    if (totalPending > 1) {
      setPendingToSave({ item, parentServiceId });
      setShowSaveAllModal(true);
    } else {
      handleSavePending(item, parentServiceId);
    }
  };

  const handleSaveAll = async () => {
    type PendingEntry = {
      item: Service | Subservice;
      parentServiceId?: string;
    };

    const allPending: PendingEntry[] = [
      ...services
        .filter((s) => s.isPending)
        .map((s) => ({ item: s as Service | Subservice })),
      ...services.flatMap((s) =>
        (s.subservices || [])
          .filter((sub) => sub.isPending)
          .map((sub) => ({
            item: sub as Service | Subservice,
            parentServiceId: s.id,
          }))
      ),
    ];

    // Validate all upfront
    const validationErrors: string[] = [];
    allPending.forEach(({ item, parentServiceId }) => {
      const isService = !parentServiceId;
      const label =
        (item as Service).name?.trim() ||
        `(unnamed ${isService ? "service" : "subservice"})`;
      const missing: string[] = [];
      if (!item.name?.trim()) missing.push("Name");
      if (isService && !(item as Service).category) missing.push("Category");
      if (isService && multipleProjectLocations && !(item as Service).location)
        missing.push("Location");
      if (!item.quantity) missing.push("Quantity");
      if (!item.hours) missing.push("Hours");
      if (item.serviceType === "professional_services" && !item.resource)
        missing.push("Resource");
      if (missing.length) {
        validationErrors.push(`"${label}": ${missing.join(", ")}`);
      }
    });

    if (validationErrors.length) {
      setResponseMessage([
        "Please fill in required fields:",
        ...validationErrors,
      ]);
      setShowFailAlert(true);
      return;
    }

    // Compute positions before any saves
    const savedServiceCountByPhase: Record<string, number> = {};
    const savedSubserviceCountByParent: Record<string, number> = {};
    services.forEach((s) => {
      if (!s.isPending) {
        savedServiceCountByPhase[s.phase] =
          (savedServiceCountByPhase[s.phase] || 0) + 1;
      }
      (s.subservices || []).forEach((sub) => {
        if (!sub.isPending) {
          savedSubserviceCountByParent[s.id] =
            (savedSubserviceCountByParent[s.id] || 0) + 1;
        }
      });
    });

    type SaveResult = {
      entry: PendingEntry;
      savedItem: Service | Subservice | null;
    };
    const results: SaveResult[] = [];

    for (const entry of allPending) {
      const { item, parentServiceId } = entry;
      const isService = !parentServiceId;
      const isMS = isService && window.location.href.includes("managed");
      const parentType = isService ? "project-phase" : "project-service";
      const phase = (item as Service)?.phase;
      const parentId = isService ? phase : parentServiceId;
      const hook = isService ? createService : createSubservice;
      let position: number;
      if (isService) {
        savedServiceCountByPhase[phase] =
          (savedServiceCountByPhase[phase] || 0) + 1;
        position = savedServiceCountByPhase[phase];
      } else {
        savedSubserviceCountByParent[parentServiceId!] =
          (savedSubserviceCountByParent[parentServiceId!] || 0) + 1;
        position = savedSubserviceCountByParent[parentServiceId!];
      }

      const data: any = {
        type: `project-${isService ? "services" : "subservices"}`,
        attributes: {
          name: item.name || "",
          "service-type": isMS ? "managed_services" : undefined,
          "task-source": "custom",
          quantity: item.quantity || undefined,
          "override-hours": item.hours ? Number(item.hours) : undefined,
          position,
        },
        relationships: {
          [parentType]: !isMS
            ? { data: { id: parentId, type: `${parentType}s` } }
            : undefined,
        },
      };

      if (isService) {
        data.relationships.project = {
          data: { id: Number(project?.id), type: "projects" },
        };
        if ((item as Service).category) {
          data.relationships["service-category"] = {
            data: {
              id: Number((item as Service).category!.value),
              type: "service-categories",
            },
          };
        }
        if ((item as Service).location) {
          data.relationships["project-location"] = {
            data: {
              id: Number((item as Service).location!.value),
              type: "project-locations",
            },
          };
        }
      }

      if (item.resource) {
        data.relationships["project-resource"] = {
          data: { id: item.resource.value, type: "project-resources" },
        };
      }

      //@ts-ignore
      const response = await hook({ slug: accountSlug, body: { data } });
      //@ts-ignore
      const updated = response?.data?.data;
      if (updated) {
        const atts = updated.attributes;
        results.push({
          entry,
          savedItem: {
            ...item,
            id: updated.id,
            name: atts.name || "",
            position: atts?.position || position,
            taskSource: atts?.["task-source"],
            isPending: false,
          },
        });
      } else {
        results.push({ entry, savedItem: null });
      }
    }

    // Single setServices call to apply all saves at once
    const successMap = new Map(
      results
        .filter((r) => r.savedItem !== null)
        .map((r) => [r.entry.item.id, r.savedItem!])
    );

    setServices(
      services.map((s) => {
        const savedService = successMap.get(s.id);
        if (savedService) return savedService as Service;
        return {
          ...s,
          subservices: (s.subservices || []).map((sub) => {
            const savedSub = successMap.get(sub.id);
            return savedSub ? (savedSub as Subservice) : sub;
          }),
        };
      })
    );

    const successCount = results.filter((r) => r.savedItem !== null).length;
    const failCount = results.filter((r) => r.savedItem === null).length;

    if (failCount === 0) {
      setResponseMessage(
        `(${successCount}) ${
          successCount === 1 ? "service" : "services"
        } created`
      );
      setShowSuccessAlert(true);
    } else {
      setResponseMessage(
        `(${successCount}) ${
          successCount === 1 ? "service" : "services"
        } created and (${failCount}) service creation ${
          failCount === 1 ? "failure" : "failures"
        }`
      );
      setShowFailAlert(true);
    }
  };

  const handleCancelPending = (pendingId: string, parentServiceId?: string) => {
    if (!parentServiceId) {
      setServices(services.filter((s) => s.id !== pendingId));
    } else {
      setServices(
        services.map((s) =>
          s.id == parentServiceId
            ? {
                ...s,
                subservices: s.subservices?.filter(
                  (sub) => sub.id !== pendingId
                ),
              }
            : s
        )
      );
    }
  };
  const editEnabled = manageServicePrivilege && project?.status === "building";
  return (
    <>
      {servicesOnPhase?.length ? (
        <table className="serviceTable">
          <thead>
            <tr>
              {columnDefs.map((col) => (
                <th
                  key={col.id}
                  className={`tableCol ${toCamelCase(col.id)}Col ${
                    col?.className || ""
                  }`}
                >
                  {col.header()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {servicesOnPhase.map((service, i) => {
              const isExpanded = expanded?.services[service.id];
              return (
                <React.Fragment key={service.id}>
                  <ServiceRow
                    service={service}
                    onSelect={(e) =>
                      setServices(
                        services.map((s) => {
                          if (s.id == service.id) {
                            return { ...s, checked: e.target.checked };
                          }
                          return s;
                        })
                      )
                    }
                    columnDefs={columnDefs}
                    draggable={draggable && editEnabled}
                    selectable={selectable && editEnabled}
                    striped={i % 2 ? true : false}
                    menuItems={
                      showSettings
                        ? [
                            ...(editEnabled
                              ? [
                                  {
                                    label: "Add Subservice",
                                    onClick: () => {
                                      setOpenMenuItem(null);
                                      handleAddPending(service.id);
                                    },
                                  },
                                ]
                              : []),
                            ...(manageStandardPrivilege
                              ? [
                                  {
                                    label: service?.standardId
                                      ? "Update Existing Standard"
                                      : "Save as Standard",
                                    onClick: () => {
                                      setServiceActionTarget(service);
                                      setShowSaveStandardModal(true);
                                    },
                                  },
                                ]
                              : []),
                            ...(editEnabled && service?.standardId
                              ? [
                                  {
                                    label: "Refresh",
                                    onClick: () => {
                                      setServiceActionTarget(service);
                                      setShowRefreshModal(true);
                                    },
                                  },
                                ]
                              : []),
                            ...(editEnabled
                              ? [
                                  {
                                    label: "Duplicate",
                                    onClick: () => {
                                      setServiceActionTarget(service);
                                      setShowDuplicateModal({
                                        all_locations: false,
                                      });
                                    },
                                  },
                                ]
                              : []),
                            ...(editEnabled && multipleProjectLocations
                              ? [
                                  {
                                    label: "Duplicate for all locations",
                                    onClick: () => {
                                      setServiceActionTarget(service);
                                      setShowDuplicateModal({
                                        all_locations: true,
                                      });
                                    },
                                  },
                                ]
                              : []),
                          ]
                        : []
                    }
                    showNotes={showNotes}
                    openMenuItem={openMenuItem}
                    setOpenMenuItem={setOpenMenuItem}
                    setOpenNotesItem={setOpenNotesItem}
                    expanded={expanded}
                    setExpanded={setExpanded}
                    onSave={() => handleCheckAndSave(service)}
                    onCancel={() => handleCancelPending(service.id)}
                  />
                  {isExpanded && (
                    <tr className={`subservicesRow ${i % 2 ? "striped" : ""}`}>
                      <td colSpan={columnDefs.length}>
                        <div
                          className={`subservices ${
                            multipleProjectLocations && draggable && selectable
                              ? "sm"
                              : ""
                          }`}
                        >
                          {service?.subservices?.length ? (
                            <SubserviceTable
                              subservices={service.subservices}
                              columnDefs={columnDefs
                                .filter(
                                  (item) =>
                                    !["service category", "location"].includes(
                                      item.id
                                    )
                                )
                                .map((item) =>
                                  item.id === "service name"
                                    ? {
                                        ...item,
                                        id: "subservice name",
                                        header: () => "SUBSERVICE NAME",
                                      }
                                    : item
                                )}
                              onReorderSubservices={async (e) =>
                                await updateSubservicePosition(e, service.id)
                              }
                              onSelect={(e, subId) =>
                                setServices(
                                  services.map((s) => {
                                    if (s.id === service.id) {
                                      return {
                                        ...s,
                                        subservices: s.subservices?.map((sub) =>
                                          sub.id == subId
                                            ? {
                                                ...sub,
                                                checked: e.target.checked,
                                              }
                                            : sub
                                        ),
                                      };
                                    }
                                    return s;
                                  })
                                )
                              }
                              draggable={draggable && editEnabled}
                              selectable={selectable && editEnabled}
                              showSettings={showSettings && editEnabled}
                              setShowDeleteServiceModal={
                                setShowDeleteServiceModal
                              }
                              setDeleteTarget={setDeleteTarget}
                              openMenuItem={openMenuItem}
                              setOpenMenuItem={setOpenMenuItem}
                              onSavePending={(sub) =>
                                handleCheckAndSave(sub, service.id)
                              }
                              onCancelPending={(subId) =>
                                handleCancelPending(subId, service.id)
                              }
                            />
                          ) : (
                            <></>
                          )}
                          {showSettings &&
                            editEnabled &&
                            manageCustomServicePrivilege && (
                              <button
                                className="addServiceBtn"
                                onClick={() => {
                                  if (!service?.id) return;
                                  handleAddPending(service.id);
                                }}
                              >
                                + Add Subservice
                              </button>
                            )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div className="placeholder">No services assigned to phase</div>
      )}
      {showSettings && editEnabled && manageCustomServicePrivilege && (
        <button
          className="addServiceBtn"
          onClick={() => handleAddPending(undefined)}
        >
          + Add Custom Service
        </button>
      )}
      {/* Settings Menu Confirmation Modals */}
      <ScopeStackModal
        modalTitle="Save as Standard"
        modalBody={
          loading ? (
            <SmallSpinner />
          ) : (
            <>
              Are you sure you want to save{" "}
              <strong>{serviceActionTarget?.name}</strong> as standard?
            </>
          )
        }
        button1Text="Cancel"
        handleButton1Click={() => {
          setShowSaveStandardModal(false);
          setServiceActionTarget(null);
        }}
        button2Text="Save"
        handleButton2Click={async () => {
          if (!serviceActionTarget?.id) return;
          setLoading(true);
          const saveStandard = async () => {
            const response = await synchronizeStandard({
              slug: accountSlug,
              id: Number(serviceActionTarget.id),
            });
            //@ts-ignore
            if (response.error) {
              setResponseMessage(
                "Something went wrong saving this service to standards"
              );
              setShowFailAlert(true);
            } else {
              setResponseMessage("Saved as standard");
              setShowSuccessAlert(true);
            }
            setShowSaveStandardModal(false);
            setLoading(false);
            setServiceActionTarget(null);
            setOpenMenuItem(null);
          };
          await saveStandard();
        }}
        button2Disabled={loading}
        show={showSaveStandardModal}
        handleClose={() => setShowSaveStandardModal(false)}
      />
      <ScopeStackModal
        modalTitle="Refresh Service"
        modalBody={
          loading ? (
            <SmallSpinner />
          ) : (
            <>
              <div>
                Are you sure you want to refresh{" "}
                <strong>{serviceActionTarget?.name}</strong>?
              </div>
              <div style={{ marginTop: "10px" }}>
                This will refresh the name, service description, and language
                fields to match the standard service.
              </div>
            </>
          )
        }
        button1Text="Cancel"
        handleButton1Click={() => {
          setShowRefreshModal(false);
          setServiceActionTarget(null);
        }}
        button2Text="Refresh"
        handleButton2Click={async () => {
          if (!serviceActionTarget?.id) return;
          setLoading(true);
          const refresh = async () => {
            const response = await refreshService({
              slug: accountSlug,
              id: Number(serviceActionTarget.id),
              body: {
                data: {
                  id: Number(serviceActionTarget.id),
                  type: "project-services",
                },
              },
            });

            await refetchServices();

            //@ts-ignore
            if (response.error) {
              setResponseMessage(
                "Something went wrong refreshing this service"
              );
              setShowFailAlert(true);
            } else {
              setResponseMessage("Service contents refreshed");
              setShowSuccessAlert(true);
            }
            setShowRefreshModal(false);
            setLoading(false);
            setServiceActionTarget(null);
            setOpenMenuItem(null);
          };
          await refresh();
        }}
        button2Disabled={loading}
        show={showRefreshModal}
        handleClose={() => setShowRefreshModal(false)}
      />
      <ScopeStackModal
        modalTitle="Duplicate Service"
        modalBody={
          loading ? (
            <SmallSpinner />
          ) : (
            <>
              Are you sure you want to duplicate{" "}
              <strong>{serviceActionTarget?.name}</strong>{" "}
              {showDuplicateModal?.all_locations
                ? "for all project locations"
                : ""}
              ?
            </>
          )
        }
        button1Text="Cancel"
        handleButton1Click={() => {
          setShowDuplicateModal(null);
          setServiceActionTarget(null);
        }}
        button2Text="Duplicate"
        handleButton2Click={async () => {
          if (!serviceActionTarget?.id || !showDuplicateModal) return;
          setLoading(true);
          const duplicate = async () => {
            const response = await duplicateService({
              slug: accountSlug,
              id: Number(serviceActionTarget.id),
              body: {
                data: {
                  id: Number(serviceActionTarget.id),
                  type: "project-services",
                  attributes: showDuplicateModal,
                },
              },
            });

            await refetchServices();

            //@ts-ignore
            if (response.error) {
              setResponseMessage(
                "Something went wrong duplicating this service"
              );
              setShowFailAlert(true);
            } else {
              setResponseMessage(
                `Service duplicated ${
                  showDuplicateModal?.all_locations
                    ? "for all project locations"
                    : ""
                }`
              );
              setShowSuccessAlert(true);
            }
            setShowDuplicateModal(null);
            setLoading(false);
            setServiceActionTarget(null);
            setOpenMenuItem(null);
          };
          await duplicate();
        }}
        button2Disabled={loading}
        show={showDuplicateModal}
        handleClose={() => setShowDuplicateModal(null)}
      />
      <ScopeStackModal
        modalTitle={`Delete ${
          deleteTarget?.type?.includes("subservices") ? "Subservice" : "Service"
        }`}
        modalBody={
          loading ? (
            <SmallSpinner />
          ) : (
            <div>
              {deleteTarget?.type?.includes("subservices") ? (
                <>
                  Are you sure you want to delete the{" "}
                  <strong>{deleteTarget?.name || "subservice"}</strong>{" "}
                  subservice?
                </>
              ) : (
                (() => {
                  const svc = services.find((s) => s.id === deleteTarget?.id);
                  const subCount = svc?.subservices?.length || 0;
                  return (
                    <>
                      You are about to delete the{" "}
                      <strong>{deleteTarget?.name || "service"}</strong> service
                      {subCount > 0 &&
                        ` with ${subCount} subservice${
                          subCount > 1 ? "s" : ""
                        }`}
                      . Are you sure you want to continue?
                    </>
                  );
                })()
              )}
            </div>
          )
        }
        button1Text="Cancel"
        handleButton1Click={() => {
          setShowDeleteServiceModal(false);
          setDeleteTarget(null);
        }}
        button2Text="Delete"
        handleButton2Click={async () => {
          if (!deleteTarget?.id) return;
          setLoading(true);
          const handleDelete = async () => {
            const isSubservice = deleteTarget?.type?.includes("subservices");
            const hook = isSubservice ? deleteSubservice : deleteService;
            const response = await hook({
              slug: accountSlug,
              id: Number(deleteTarget?.id),
            });

            await refetchServices();

            //@ts-ignore
            if (response.error) {
              setResponseMessage(
                `Something went wrong deleting this ${
                  isSubservice ? "subservice" : "service"
                }`
              );
              setShowFailAlert(true);
            } else {
              setResponseMessage(
                `${isSubservice ? "Subservice" : "Service"} deleted`
              );
              setShowSuccessAlert(true);
            }
            setShowDeleteServiceModal(false);
            setLoading(false);
            setOpenMenuItem(null);
            setDeleteTarget(null);
          };
          await handleDelete();
        }}
        button2Disabled={loading}
        show={showDeleteServiceModal}
        handleClose={() => {
          setShowDeleteServiceModal(false);
          setDeleteTarget(null);
        }}
        deleteModal={true}
      />
      <ManageNotesSlideout
        resource={openNotesItem}
        projectId={project?.id ?? null}
        onClose={() => {
          setOpenNotesItem(null);
          refetchServices();
        }}
      />

      {handleAlert(
        showFailAlert,
        responseMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      {handleAlert(
        showSuccessAlert,
        responseMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      <ScopeStackModal
        show={showSaveAllModal}
        modalTitle="Save Pending Services"
        modalBody={
          saveModalLoading ? (
            <SmallSpinner />
          ) : (
            <p>Do you want to save all pending services?</p>
          )
        }
        button1Text={saveModalLoading ? undefined : "Just This One"}
        handleButton1Click={async () => {
          setSaveModalLoading(true);
          if (pendingToSave) {
            await handleSavePending(
              pendingToSave.item,
              pendingToSave.parentServiceId
            );
          }
          setSaveModalLoading(false);
          setShowSaveAllModal(false);
          setPendingToSave(null);
        }}
        button2Text={saveModalLoading ? undefined : "Save All"}
        handleButton2Click={async () => {
          setSaveModalLoading(true);
          await handleSaveAll();
          setSaveModalLoading(false);
          setShowSaveAllModal(false);
          setPendingToSave(null);
        }}
        handleClose={() => {
          if (saveModalLoading) return;
          setShowSaveAllModal(false);
          setPendingToSave(null);
        }}
      />
    </>
  );
};

const ServiceRow = ({
  service,
  expanded,
  setExpanded,
  onSelect,
  columnDefs,
  draggable,
  selectable,
  striped,
  menuItems,
  showNotes,
  openMenuItem,
  setOpenMenuItem,
  setOpenNotesItem,
  onSave,
  onCancel,
}: {
  service: Service;
  expanded: ExpandedState;
  setExpanded: React.Dispatch<SetStateAction<ExpandedState>>;
  onSelect: (e) => void;
  columnDefs: ColumnDef<Service>[];
  draggable: boolean;
  selectable: boolean;
  striped: boolean;
  menuItems: { label: string; onClick: () => void }[];
  showNotes: boolean;
  openMenuItem: Service | Subservice | null;
  setOpenMenuItem: (item: Service | Subservice | null) => void;
  setOpenNotesItem: (item: Service | null) => void;
  onSave: () => void;
  onCancel: () => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: `service:${service.id}`,
    });
  const isPlaceholder = service?.id == "-1";
  const isPending = service?.isPending === true;
  const toggleExpand = () => {
    setExpanded((prev) => ({
      ...prev,
      services: {
        ...prev.services,
        [service.id]: !prev.services[service.id],
      },
    }));
  };

  return (
    <tr
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        backgroundColor: striped ? "#FAFAFA" : "#fff",
      }}
    >
      {columnDefs.map((col, index) => {
        const isLast = index === columnDefs.length - 1;
        return (
          <td
            key={col.id}
            className={col?.id?.includes("name") ? "nameField" : ""}
          >
            <div className="serviceTd">
              {index == 0 && isPending && !isPlaceholder && (
                <div className="submitBtns">
                  <FontAwesomeIcon
                    icon={faCheck}
                    onClick={onSave}
                    style={{ color: "#418172" }}
                    title="Save"
                  />
                  <FontAwesomeIcon
                    icon={faXmark}
                    onClick={onCancel}
                    style={{ color: "#CC672F" }}
                    title="Cancel"
                  />
                </div>
              )}
              {index == 0 && draggable && !isPlaceholder && !isPending && (
                <span {...attributes} {...listeners} className="dndHandle">
                  <FontAwesomeIcon icon={faGripDotsVertical} />
                </span>
              )}
              {index == 0 && selectable && !isPlaceholder && !isPending && (
                <input
                  type="checkbox"
                  style={{ accentColor: "#418172" }}
                  checked={service.checked}
                  onChange={onSelect}
                />
              )}
              {index == 0 && !isPlaceholder && !isPending && (
                <FontAwesomeIcon
                  icon={
                    expanded.services[service.id] ? faSquareMinus : faSquarePlus
                  }
                  size="lg"
                  className={`serviceToggle ${
                    !draggable && !selectable ? "first" : ""
                  }`}
                  onClick={() => {
                    //prevent unintended autoscroll
                    const scrollY = window.scrollY;
                    requestAnimationFrame(() => {
                      window.scrollTo({ top: scrollY, behavior: "auto" });
                    });
                    toggleExpand();
                  }}
                />
              )}

              {col.cell(service)}

              {isLast && !isPlaceholder && (
                <>
                  {isPending ? (
                    <div className="settingsContainer empty"></div>
                  ) : (
                    <div className="settingsContainer">
                      {showNotes && (
                        <FontAwesomeIcon
                          icon={faMessageCaptions}
                          className="notesIcon"
                          onClick={() => setOpenNotesItem(service)}
                          style={{
                            cursor: "pointer",
                            color: service?.notes?.length
                              ? "#CC672F"
                              : "#8F8F8F",
                          }}
                        />
                      )}
                      {menuItems?.length > 0 && (
                        <div style={{ position: "relative" }}>
                          <FontAwesomeIcon
                            icon={faGear}
                            onClick={() =>
                              //@ts-ignore
                              setOpenMenuItem((prev) =>
                                prev?.id === service?.id ? null : service
                              )
                            }
                            style={{ cursor: "pointer" }}
                          />
                          {openMenuItem?.type?.includes("services") &&
                            openMenuItem?.id == service.id && (
                              <div className="cogMenu service">
                                {menuItems.map((item) => {
                                  return (
                                    <div
                                      key={item.label}
                                      className={`cogMenuItem ${
                                        item.label.includes("Standard")
                                          ? "standard"
                                          : ""
                                      }`}
                                      onClick={item.onClick}
                                    >
                                      {item.label}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </td>
        );
      })}
    </tr>
  );
};

const SubserviceTable = ({
  subservices,
  columnDefs,
  draggable,
  selectable,
  onReorderSubservices,
  onSelect,
  showSettings,
  setShowDeleteServiceModal,
  setDeleteTarget,
  openMenuItem,
  setOpenMenuItem,
  onSavePending,
  onCancelPending,
}: {
  subservices: Subservice[];
  columnDefs: ColumnDef<Service>[];
  draggable: boolean;
  selectable: boolean;
  onReorderSubservices: (newOrder: Subservice[]) => void;
  onSelect: (e, subId) => void;
  showSettings: boolean;
  setShowDeleteServiceModal: (bool: boolean) => void;
  setDeleteTarget: (target: Service | Subservice | null) => void;
  openMenuItem: Subservice | null;
  setOpenMenuItem: (service) => void;
  onSavePending: (sub: Subservice) => void;
  onCancelPending: (subId: string) => void;
}) => {
  const [activeSubservice, setActiveSubservice] = useState<Subservice | null>(
    null
  );
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    if (!draggable) return;
    const sub = subservices.find(
      (s) => `subservice:${s.id}` === event.active.id
    );
    if (sub) setActiveSubservice(sub);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      setActiveSubservice(null);
      return;
    }

    const oldIndex = subservices.findIndex(
      (s) => `subservice:${s.id}` === active.id
    );
    let overSub = subservices.find((s) => `subservice:${s.id}` === over.id);

    // Don't allow dropping onto or after a pending subservice
    if (overSub?.isPending) {
      const savedSubs = subservices.filter((s) => !s.isPending);
      if (!savedSubs.length) {
        setActiveSubservice(null);
        return;
      }
      overSub = savedSubs[savedSubs.length - 1];
    }

    const newIndex = subservices.findIndex((s) => s.id === overSub!.id);

    const reordered = arrayMove(subservices, oldIndex, newIndex).map(
      (s, i) => ({
        ...s,
        position: i + 1,
      })
    );
    onReorderSubservices(reordered);
    setActiveSubservice(null);
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={subservices.map((s) => `subservice:${s.id}`)}
        strategy={verticalListSortingStrategy}
      >
        <table className={`serviceTable `}>
          <thead>
            <tr>
              {columnDefs.map((col) => (
                <th
                  key={col.id}
                  className={`tableCol ${toCamelCase(col.id)}Col ${
                    col.className || ""
                  }`}
                >
                  {col.header()}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {[...subservices]
              .sort((a, b) => {
                if (a.isPending && !b.isPending) return 1;
                if (!a.isPending && b.isPending) return -1;
                return (a.position || 0) - (b.position || 0);
              })
              .map((sub, i) => (
                <SubserviceRow
                  key={sub.id}
                  subservice={sub}
                  columnDefs={columnDefs}
                  draggable={draggable}
                  selectable={selectable}
                  onSelect={onSelect}
                  striped={i % 2 ? true : false}
                  setShowDeleteServiceModal={setShowDeleteServiceModal}
                  setDeleteTarget={setDeleteTarget}
                  showSettings={showSettings}
                  openMenuItem={openMenuItem}
                  setOpenMenuItem={setOpenMenuItem}
                  onSave={() => onSavePending(sub)}
                  onCancel={() => onCancelPending(sub.id)}
                />
              ))}
          </tbody>
        </table>
      </SortableContext>
      <DragOverlay adjustScale={false}>
        {activeSubservice && (
          <ServiceOverlay
            service={activeSubservice as Service}
            columnDefs={columnDefs}
            selectable={selectable}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
};

const SubserviceRow = ({
  subservice,
  columnDefs,
  draggable,
  selectable,
  onSelect,
  striped,
  showSettings,
  setShowDeleteServiceModal,
  setDeleteTarget,
  openMenuItem,
  setOpenMenuItem,
  onSave,
  onCancel,
}: {
  subservice: Subservice;
  columnDefs: ColumnDef<Service>[];
  draggable: boolean;
  selectable: boolean;
  onSelect: (e, id) => void;
  striped: boolean;
  showSettings: boolean;
  setShowDeleteServiceModal: (bool: boolean) => void;
  setDeleteTarget: (target: Service | Subservice | null) => void;
  openMenuItem: Subservice | null;
  setOpenMenuItem: (service) => void;
  onSave: () => void;
  onCancel: () => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: `subservice:${subservice.id}` });

  const isPlaceholder = subservice.id == "-1";
  const isPending = subservice?.isPending === true;

  return (
    <tr
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        backgroundColor: striped ? "#FAFAFA" : "#fff",
      }}
    >
      {columnDefs.map((col, i) => {
        const isLast = i === columnDefs.length - 1;
        return (
          <td
            key={col.id}
            className={col?.id?.includes("name") ? "nameField" : ""}
          >
            <div className="serviceTd">
              {i === 0 && !isPlaceholder && isPending && (
                <div className="submitBtns subservices">
                  <FontAwesomeIcon
                    icon={faCheck}
                    onClick={onSave}
                    style={{ color: "#418172" }}
                    title="Save"
                  />
                  <FontAwesomeIcon
                    icon={faXmark}
                    onClick={onCancel}
                    style={{ color: "#CC672F" }}
                    title="Cancel"
                  />
                </div>
              )}
              {i === 0 && !isPlaceholder && !isPending && (
                <>
                  {draggable && (
                    <span {...attributes} {...listeners} className="dndHandle">
                      <FontAwesomeIcon icon={faGripDotsVertical} />
                    </span>
                  )}
                  {selectable && (
                    <input
                      type="checkbox"
                      style={{ accentColor: "#418172" }}
                      checked={subservice.checked}
                      onChange={(e) => onSelect(e, subservice.id)}
                    />
                  )}
                </>
              )}
              {col.cell(subservice as Service)}
            </div>
          </td>
        );
      })}
    </tr>
  );
};
